'use client';
import { useResource } from '@/lib/use-resource';
import { StockStoryButton } from '@/components/StockStoryButton';

interface Position {
  symbol: string;
  name: string;
  sector: string;
  weight: number;
  composite: number;
  cluster: number;
  pairwiseAvgCorr: number;
}
interface DroppedCommodity {
  symbol: string;
  sector: string;
  composite: number;
}
interface Recommended {
  generatedAt: string;
  asOfScoresDate: string;
  cell: {
    C_floor: number;
    k: number;
    MAX_CORR: number;
    MAX_CLUSTER_WEIGHT: number;
    NUM_CLUSTERS: number;
    MAX_WEIGHT: number;
  };
  rule: string;
  excludedSectors: string[];
  universeSize: number;
  afterCommodityFilter: number;
  aboveFloor: number;
  basketSize: number;
  positions: Position[];
  droppedCommodityNames: DroppedCommodity[];
  error?: string;
}

const CLUSTER_COLORS = [
  '#60a5fa', // blue
  '#22c55e', // green
  '#a855f7', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#06b6d4', // cyan
];

export function RecommendedPortfolio() {
  const { data, error } = useResource<Recommended>('/api/recommended-portfolio');

  if (error) return <div className="text-[11px] text-red-400">Failed to load recommended portfolio.</div>;
  if (!data) return <div className="text-[11px] text-[#64748b]">Loading recommended portfolio…</div>;
  if (data.error) return <div className="text-[11px] text-amber-400">{data.error}</div>;

  // Cluster weight aggregation for the cluster-weight-by-color row
  const clusterWeights = new Map<number, number>();
  for (const p of data.positions) clusterWeights.set(p.cluster, (clusterWeights.get(p.cluster) ?? 0) + p.weight);
  const clusterEntries = [...clusterWeights.entries()].sort((a, b) => b[1] - a[1]);

  return (
    <section className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-[#1e293b] flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-amber-300 font-semibold">
            Recommended Portfolio (research output)
          </div>
          <div className="text-[10px] text-[#64748b] mt-0.5">{data.rule}</div>
        </div>
        <div className="text-[10px] text-[#64748b]">
          {data.basketSize} positions · universe {data.universeSize} → {data.afterCommodityFilter} after commodity filter → {data.aboveFloor} above floor
        </div>
      </div>

      {/* Cluster weight bar */}
      <div className="px-3 py-2 border-b border-[#1e293b]">
        <div className="text-[9px] uppercase tracking-wider text-[#64748b] mb-1">Cluster mix</div>
        <div className="flex h-3 rounded overflow-hidden border border-[#1e293b]">
          {clusterEntries.map(([cluster, weight]) => (
            <div
              key={cluster}
              style={{
                width: `${weight * 100}%`,
                background: CLUSTER_COLORS[cluster % CLUSTER_COLORS.length],
              }}
              title={`Cluster ${cluster}: ${(weight * 100).toFixed(1)}%`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[#94a3b8] mt-1">
          {clusterEntries.map(([cluster, weight]) => (
            <span key={cluster} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded" style={{ background: CLUSTER_COLORS[cluster % CLUSTER_COLORS.length] }} />
              cluster {cluster}: {(weight * 100).toFixed(1)}%
            </span>
          ))}
        </div>
      </div>

      <div className="px-3 py-2">
        <div className="grid grid-cols-[28px_60px_1fr_120px_72px_70px_50px_60px] gap-2 text-[9px] uppercase tracking-wider text-[#64748b] py-1 border-b border-[#1e293b]">
          <div>#</div>
          <div>SYM</div>
          <div>NAME</div>
          <div>SECTOR</div>
          <div className="text-right">WEIGHT</div>
          <div className="text-right">C</div>
          <div className="text-right">CLU</div>
          <div className="text-right">ρ̄</div>
        </div>
        {data.positions.map((p, i) => (
          <div
            key={p.symbol}
            className="grid grid-cols-[28px_60px_1fr_120px_72px_70px_50px_60px] gap-2 text-[11px] py-0.5 leading-tight items-center border-b border-[#1e293b]/40"
          >
            <div className="text-[#64748b] tabular-nums">{i + 1}</div>
            <div className="font-mono font-bold text-amber-300 flex items-center gap-1">
              {p.symbol}
              <StockStoryButton symbol={p.symbol} />
            </div>
            <div className="truncate text-[#cbd5e1]">{p.name}</div>
            <div className="truncate text-[10px] text-[#94a3b8]">{p.sector}</div>
            <div className="text-right tabular-nums font-mono text-emerald-300 font-semibold">{(p.weight * 100).toFixed(2)}%</div>
            <div className="text-right tabular-nums text-[#cbd5e1]">{p.composite.toFixed(2)}</div>
            <div className="text-right">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ background: CLUSTER_COLORS[p.cluster % CLUSTER_COLORS.length] }}
                title={`Cluster ${p.cluster}`}
              />
            </div>
            <div className="text-right tabular-nums text-[10px] text-[#94a3b8]">{p.pairwiseAvgCorr.toFixed(2)}</div>
          </div>
        ))}
      </div>

      {data.droppedCommodityNames.length > 0 && (
        <div className="px-3 py-2 border-t border-[#1e293b] bg-[#0a0e14]">
          <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1">
            Dropped by commodity filter ({data.droppedCommodityNames.length})
          </div>
          <div className="flex flex-wrap gap-2 text-[10px]">
            {data.droppedCommodityNames.map(d => (
              <span key={d.symbol} className="font-mono text-[#94a3b8]">
                <span className="text-red-300/70 line-through decoration-red-500/40">{d.symbol}</span>
                <span className="text-[#64748b]"> · {d.sector} · C {d.composite.toFixed(2)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="px-3 py-2 border-t border-[#1e293b] text-[10px] text-[#64748b] leading-relaxed">
        <strong className="text-[#94a3b8]">Research output, not yet driving the live alpha basket.</strong> The
        live basket above this panel uses top-10 conviction-weighted, monthly-rebalanced. This is the conviction-
        weighted, cluster-capped recommendation from the after-tax Sharpe sweep over 2021–2023 vintages.
        Cluster colors group stocks that move together (252-day daily-return correlation). Per-name cap{' '}
        {(data.cell.MAX_WEIGHT * 100).toFixed(0)}%. Per-cluster cap {(data.cell.MAX_CLUSTER_WEIGHT * 100).toFixed(0)}%.
      </div>
    </section>
  );
}
